import { useCallback, useMemo, useRef, useState } from 'react'
import type { PoolContents } from '../../../Services/OrderWizard/pools.ts'
import type { ClickModifiers, Selection } from '../../../Services/OrderWizard/poolBoard.ts'
import { checkboxSelection, clickSelection, NO_SELECTION, selectionByColumn } from '../../../Services/OrderWizard/poolBoard.ts'
import { useLatest } from '../../../Framework/View/Hooks/useLatest.ts'

/**
 * The pool board's selection, by click or checkbox, with stable handlers for the memoised rows and each
 * column's part of it. `columnOf` gives an item's column keys, where a shift-click range is taken from.
 */
export const usePoolSelection = (contents: readonly PoolContents[], columnOf: (key: string) => string[]) => {
  const [selection, setSelection] = useState<Selection>(NO_SELECTION)
  const column = useLatest(columnOf)
  const onClickItem = useCallback(
    (key: string, modifiers: ClickModifiers) => {
      setSelection((current) => clickSelection(current, key, column.current(key), modifiers))
    },
    [column]
  )
  const onCheck = useCallback(
    (key: string, shift: boolean) => {
      setSelection((current) => checkboxSelection(current, key, column.current(key), shift))
    },
    [column]
  )
  const clear = useCallback(() => setSelection(NO_SELECTION), [])
  const previous = useRef(new Map<number, ReadonlySet<string>>())
  const byColumn = useMemo(() => selectionByColumn(selection.keys, contents, previous.current), [selection.keys, contents])
  previous.current = byColumn
  return { selection, byColumn, onClickItem, onCheck, clear }
}
